// src/FoundItemsPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, orderBy, where } from "firebase/firestore";
import { db } from "./firebase";
import AOS from "aos";
import "aos/dist/aos.css";
import MessageBox from "./MessageBox";
import "./Homepage.css";

const FoundItemsPage = () => {
  const [allFoundItems, setAllFoundItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const showMessageBox = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
  };

  const closeMessageBox = () => {
    setMessage("");
    setMessageType("info");
  };

  const fetchFoundItems = useCallback(async () => {
    setLoading(true);
    try {
      let q = query(
        collection(db, "items"),
        where("type", "==", "found"),
        where("status", "==", "unclaimed"),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(q);
      const itemsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setAllFoundItems(itemsData);
      setFilteredItems(itemsData);
    } catch (err) {
      console.error("Error fetching found items:", err);
      showMessageBox(
        "Error loading found items: Please ensure required Firestore indexes are created for 'items' collection (for 'type, status, timestamp').",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFoundItems();
  }, [fetchFoundItems]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(allFoundItems);
    } else {
      const filtered = allFoundItems.filter(
        (item) =>
          item.itemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery, allFoundItems]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  if (loading) {
  return (
    <div className="min-h-screen flex flex-col bg-white animate-pulse select-none">

      {/* Navbar Skeleton */}
      <nav className="flex items-center bg-gray-100 border-b border-gray-300 px-5 h-20">
        {/* Logo placeholder */}
        <div className="bg-gray-300 rounded-md w-36 h-10" />
        {/* Spacer */}
        <div className="ml-auto flex items-center">
          {/* Back to Home button placeholder */}
          <div className="bg-gray-300 rounded-full w-32 h-10" />
        </div>
      </nav>

      {/* Gradient hr/horizontal rule */}
      <div className="h-1 bg-gradient-to-r from-red-200 via-yellow-200 to-red-200 my-0" />

      {/* Main Content Container */}
      <main className="max-w-7xl mx-auto my-10 px-4 space-y-8">

        {/* Page Title */}
        <div className="h-8 w-64 bg-gray-300 rounded-md mx-auto" />

        {/* Search Form Skeleton */}
        <form className="flex justify-center items-center gap-3 max-w-sm mx-auto">
          <input
            type="text"
            className="bg-gray-300 rounded-full w-full h-12"
            disabled
            placeholder=""
            aria-hidden="true"
          />
          <button
            type="submit"
            className="bg-gray-300 rounded-full w-28 h-12"
            disabled
            aria-hidden="true"
          />
        </form>

        {/* Search info text placeholder */}
        <div className="h-6 w-48 bg-gray-300 rounded-full mx-auto" />

        {/* Items Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
          {[...Array(6)].map((_, idx) => (
            <div
              key={idx}
              className="bg-gray-100 w-full max-w-xs rounded-3xl shadow-lg border border-red-100 overflow-hidden flex flex-col"
            >
              {/* Image placeholder */}
              <div className="h-44 bg-gray-300 w-full rounded-t-3xl" />
              {/* Content placeholder */}
              <div className="flex flex-col p-5 gap-3 flex-1">
                <div className="h-6 bg-gray-300 rounded w-3/4" />
                <div className="h-4 bg-gray-300 rounded w-full" />
                <div className="h-4 bg-gray-300 rounded w-full" />
                <div className="h-4 bg-gray-300 rounded w-1/2" />
                <div className="h-10 bg-gray-300 rounded w-32 mt-auto" />
              </div>
            </div>
          ))}
        </div>

      </main>
    </div>
  );
}


  return (
    <>
      <nav className="flex items-center bg-white border-b px-5 shadow-md h-20">
        <Link className="mr-4" to="/homepage">
          <img
            src="/Christ_logo_latest.png"
            alt="Christ University Logo"
            className="logo-christ max-w-[150px] h-auto"
          />
        </Link>
        <div className="ml-auto flex items-center">
          <Link
            to="/homepage"
            className="bg-green-600 text-white my-nav-link font-medium border border-red-700 border-b-4 px-4 py-2 rounded-full hover:brightness-110 hover:border-t-4 hover:border-b active:opacity-80 transition duration-300"
          >
            Back to Home
          </Link>
        </div>
      </nav>

      <hr className="border-0 h-1 bg-gradient-to-r from-blue-200 via-green-200 to-blue-200 my-0" data-aos="zoom-in" data-aos-duration="1160" />

      <div className="max-w-7xl mx-auto my-10 px-4" data-aos="fade-up">
        {message && (
          <MessageBox message={message} type={messageType} onClose={closeMessageBox} />
        )}

        <h3 className="text-center mb-8 text-green-700 font-bold text-2xl">All Found Items</h3>

        <form onSubmit={handleSearch} className="flex justify-center items-center mb-8 gap-3">
          <input
            type="text"
            className="rounded-full px-5 py-3 border border-gray-300 shadow-sm focus:ring focus:ring-green-100 w-full max-w-xs text-gray-700"
            placeholder="Search found items..."
            value={searchQuery}
            onChange={handleSearchInputChange}
          />
          <button
            type="submit"
            className="bg-green-600 text-white rounded-pill px-6 py-3 font-semibold hover:bg-green-700 transition"
          >
            Search
          </button>
        </form>

        {searchQuery && (
          <div className="text-center mb-4">
            <span className="text-gray-500 text-sm">
              Showing {filteredItems.length} of {allFoundItems.length} items
              {searchQuery && ` for "${searchQuery}"`}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div
                key={item.id}
                className="bg-white w-full max-w-xs flex flex-col rounded-3xl shadow-lg overflow-hidden border border-green-100"
                data-aos="fade-up"
              >
                {item.imageUrl && (
                  <img
                    src={item.imageUrl}
                    className="w-full h-44 object-cover"
                    alt={item.itemName}
                  />
                )}
                <div className="flex flex-col p-5 gap-2 flex-1">
                  <h5 className="text-green-700 font-semibold text-lg mb-2">
                    {item.itemName}
                  </h5>
                  <p className="text-gray-700 flex-grow line-clamp-3 mb-1">
                    {item.description}
                  </p>
                  <ul className="text-xs text-gray-500 mb-2 space-y-1">
                    <li>
                      <span className="font-semibold">Category:</span> {item.category}
                    </li>
                    <li>
                      <span className="font-semibold">Location:</span> {item.location}
                    </li>
                    {item.building && (
                      <li>
                        <span className="font-semibold">Building:</span> {item.building}
                      </li>
                    )}
                    {item.classroom && (
                      <li>
                        <span className="font-semibold">Classroom:</span> {item.classroom}
                      </li>
                    )}
                    <li>
                      <span className="font-semibold">Date Found:</span> {item.reportedDate}
                    </li>
                  </ul>
                  <Link 
  to={`/itemdetails/${item.id}`}
  className="my-nav-link relative w-fit no-underline flex items-center px-5 py-2 overflow-hidden font-medium transition-all bg-green-600 rounded-md group mt-auto"
  data-aos="zoom-in-up"
 // optional dynamic delay
>
  <span className="absolute top-0 right-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-800 rounded group-hover:-mr-4 group-hover:-mt-4">
    <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
  </span>
  <span className="absolute bottom-0 rotate-180 left-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-800 rounded group-hover:-ml-4 group-hover:-mb-4">
    <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
  </span>
  <span className="absolute bottom-0 left-0 w-full h-full transition-all duration-500 ease-in-out delay-200 -translate-x-full bg-green-700 rounded-md group-hover:translate-x-0"></span>
  <span className="relative text-white transition-colors duration-200 ease-in-out group-hover:text-white">
    View Details
  </span>
</Link>

                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center">
              <p className="text-gray-400 italic">
                {searchQuery ? `No found items found matching "${searchQuery}".` : "No found items found."}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FoundItemsPage;
